// plainError v1.0 (2026-09-30, cc_tasks row 353, T395) — no machine code reaches a user.
//
// Every toast in the app passes through here (installPlainToastGuard patches the sonner
// singleton once at boot; hooks/use-toast.ts calls plainUserText for the shadcn toast).
// What it does to a message:
//   1. A known code (sign_in_required, insufficient_credits, ...) becomes the sentence
//      for it, with the one thing that fixes it.
//   2. Any other bare snake_case word is dropped, never shown.
//   3. A raw technical dump after "<Label> failed:" (HTTP status, database error text,
//      stack-ish text, JSON) becomes "<Label> did not work. Try again in a minute."
// The original text always goes to the console, so the code stays in logs.

const CODE_SENTENCES: Record<string, string> = {
  sign_in_required: "You are signed out. Sign in again, then try once more.",
  sing_in_required: "You are signed out. Sign in again, then try once more.",
  not_signed_in: "You are signed out. Sign in again, then try once more.",
  unauthorized: "You are signed out. Sign in again, then try once more.",
  forbidden: "This account does not have access to that. Switch to the account that owns it.",
  not_owner: "This account does not have access to that. Switch to the account that owns it.",
  mailbox_reconnect_required: "Your email connection needs to be renewed. Reconnect it in Sender settings, then try again.",
  insufficient_credits: "You are out of credits for this. Add credits on the Pricing page, then try again.",
  credits_exhausted: "You are out of credits for this. Add credits on the Pricing page, then try again.",
  budget_exceeded: "This has hit today's spending limit. Try again tomorrow.",
  rate_limited: "Too many tries in a short time. Wait a minute, then try again.",
  daily_cap: "You have reached today's limit for this. It resets tomorrow.",
  missing_fields: "Some required fields are empty. Fill them in, then try again.",
  missing_inputs: "Some required details are missing. Add them, then try again.",
  missing_input: "Some required details are missing. Add them, then try again.",
  invalid_input: "Something in the form is not valid. Check it, then try again.",
  not_found: "That item could not be found. It may have been deleted; refresh the page.",
  no_sending_identity: "There is no verified sending address on this account yet. Set one up in Sender settings, then send again.",
  unsubstituted_tokens: "The message still has a placeholder in it (text in brackets). Replace it, then send again.",
  provider_rejected: "The mail provider did not accept this message. Check the address, then try again in a minute.",
  send_error: "Something went wrong while sending. Try again in a minute.",
  timeout: "This took too long and stopped. Try again in a minute.",
  internal_error: "Something went wrong on our side. Try again in a minute.",
  server_error: "Something went wrong on our side. Try again in a minute.",
  upstream_error: "Something went wrong on our side. Try again in a minute.",
  insufficient_data: "There is not enough information yet to do this.",
  insufficient_evidence: "There is not enough information yet to do this.",
  insufficient_data_for_template: "There is not enough information yet to build this card.",
  already_exists: "That already exists.",
  duplicate: "That already exists.",
  plan_required: "This needs a paid plan. See the Pricing page to upgrade.",
  upgrade_required: "This needs a paid plan. See the Pricing page to upgrade.",
};

const GENERIC = "Something went wrong. Try again in a minute.";
const CODE_RE = /\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b/g;
// Technical text that is never shown: HTTP codes, database/driver errors, stack-ish or
// JSON dumps, framework error names.
const TECH_RE = /\b(?:[1-5]\d\d)\b.*(?:error|status)|API \d{3}|Edge Function|non-2xx|FunctionsHttpError|FunctionsFetchError|TypeError|SyntaxError|ReferenceError|Failed to fetch|NetworkError|violates|duplicate key|relation ".*"|column ".*"|PGRST\d+|JWT|\bpgrst|\{\s*"|\bundefined\b|\bnull\b|\[object Object\]/i;

function isUrlish(s: string): boolean { return /https?:\/\/|www\.|@|\/[a-z]|\w\.[a-z]{2,4}\b/i.test(s); }

/** One user-facing sentence for whatever came back. Never returns a code. */
export function plainUserText(input: unknown, fallback = GENERIC): string {
  if (input == null) return fallback;
  let s = typeof input === "string" ? input : (input as any)?.message ?? (input as any)?.error ?? String(input);
  if (typeof s !== "string") s = String(s);
  const original = s;
  s = s.trim();
  if (!s) return fallback;

  // A bare code.
  if (/^[a-z][a-z0-9]*(_[a-z0-9]+)+$/.test(s)) {
    console.warn("[plainError] code hidden from user:", s);
    return CODE_SENTENCES[s] || fallback;
  }

  // "<Label> failed: <detail>" / "<Label> failed - <detail>"
  const m = s.match(/^(.{1,60}?)\s+failed\s*[:\-–—]\s*(.+)$/i) || s.match(/^(Failed|Error)\s*[:\-–—]\s*(.+)$/);
  if (m) {
    const label = /^(failed|error)$/i.test(m[1]) ? "That" : m[1];
    const detail = m[2].trim();
    const known = CODE_SENTENCES[detail.replace(/[.\s]+$/, "")];
    const tail = known || (TECH_RE.test(detail) || CODE_RE.test(detail) || /^(unknown|unknown error|undefined|null)\.?$/i.test(detail)
      ? "Try again in a minute."
      : detail);
    CODE_RE.lastIndex = 0;
    const out = `${label} did not work. ${tail}`;
    if (out !== original) console.warn("[plainError] shown:", out, "| original:", original);
    return out;
  }

  // Codes inside a sentence: swap known ones, drop the rest (never inside URLs/emails).
  if (CODE_RE.test(s) && !isUrlish(s)) {
    CODE_RE.lastIndex = 0;
    let replacedKnown = false;
    const out = s.replace(CODE_RE, (c) => {
      if (CODE_SENTENCES[c]) { replacedKnown = true; return CODE_SENTENCES[c]; }
      return "";
    }).replace(/[:;]\s*(,\s*)+/g, ": ").replace(/(,\s*){2,}/g, ", ").replace(/\s{2,}/g, " ")
      .replace(/\s+([.,:;])/g, "$1").replace(/[\s,:;]+$/, "").replace(/[\s,:;]+\./g, ".").trim();
    console.warn("[plainError] code hidden from user:", original);
    if (!out || out.length < 4) return fallback;
    return replacedKnown || /[.!?]$/.test(out) ? out : `${out}.`;
  }
  CODE_RE.lastIndex = 0;

  if (TECH_RE.test(s) && s.length > 0 && !isUrlish(s)) {
    console.warn("[plainError] technical text hidden from user:", original);
    return fallback;
  }
  return s;
}

function cleanNode(v: unknown): unknown {
  return typeof v === "string" ? plainUserText(v) : v;
}

let installed = false;
/**
 * Wrap the sonner toast singleton so every toast.error / warning / info / message /
 * success / plain toast() string goes through plainUserText. Call once at boot.
 */
export function installPlainToastGuard(t: any): void {
  if (installed || !t) return;
  installed = true;
  for (const k of ["error", "warning", "info", "message", "success", "loading"]) {
    const orig = t[k];
    if (typeof orig !== "function") continue;
    t[k] = (msg: unknown, opts?: any) => {
      const o = opts && typeof opts.description === "string" ? { ...opts, description: cleanNode(opts.description) } : opts;
      return orig.call(t, cleanNode(msg), o);
    };
  }
}
